import { applyMove } from "@dailywars/core/engine";
import type { Coord, GameState, Move, UnitKind } from "@dailywars/core/engine";
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";

import { useGameSelector, useGameStoreApi } from "#/game/GameStoreProvider";
import type { GameStore, GameStoreApi } from "#/game/store";
import { canEndTurnFromState, canUndoFromState } from "#/game/store/selectors";

import { TUTORIAL_STEPS, type TutorialPrompt, type TutorialStep } from "./tutorialSteps";

function resolveAiTurnMoves(script: TutorialStep["aiTurnMoves"], state: GameState): Move[] {
  if (!script) return [];
  return typeof script === "function" ? script(state) : script;
}

function currentStep(stepIndexRef: RefObject<number>): TutorialStep {
  return TUTORIAL_STEPS[stepIndexRef.current]!;
}

export interface TutorialController {
  prompt: TutorialPrompt;
  isFinal: boolean;
  blocksInteraction: boolean;
  canEndTurn: boolean;
  canUndo: boolean;
  selectTile: (tile: Coord) => void;
  chooseBuild: (pos: Coord, kind: UnitKind) => void;
  endTurn: () => void;
  undoLastMove: () => void;
  dismissCoach: () => void;
  buildKind: UnitKind | null;
}

function submitTutorialAiTurn(store: GameStore, aiTurnMoves: TutorialStep["aiTurnMoves"]): void {
  let finalState = applyMove(store.localState, { type: "endTurn" }).state;
  const moves = resolveAiTurnMoves(aiTurnMoves, finalState);
  for (const move of moves) {
    finalState = applyMove(finalState, move).state;
  }
  store.submitFinish({
    finalState,
    aiMoves: moves,
    finalServerDayNumber: finalState.currentDay,
    includePlayerEndTurnPlayback: true,
  });
}

function useTutorialStepIndex(api: GameStoreApi) {
  const [stepIndex, setStepIndex] = useState(0);
  const stepIndexRef = useRef(stepIndex);
  stepIndexRef.current = stepIndex;

  useEffect(() => {
    const check = () => {
      const step = TUTORIAL_STEPS[stepIndexRef.current];
      if (!step || step.isFinal || step.dismissible) return;
      if (step.isComplete(api.getState())) {
        setStepIndex((i) => Math.min(i + 1, TUTORIAL_STEPS.length - 1));
      }
    };
    check();
    return api.subscribe(check);
  }, [api]);

  return { stepIndex, stepIndexRef, setStepIndex };
}

function useTutorialUndo(api: GameStoreApi, stepIndexRef: RefObject<number>) {
  return useCallback(() => {
    const step = currentStep(stepIndexRef);
    if (step.dismissible || !step.enableUndo) return;
    const store = api.getState();
    if (!canUndoFromState(store)) return;
    store.undoLastMove();
  }, [api, stepIndexRef]);
}

function useTutorialActions({
  api,
  stepIndexRef,
  setStepIndex,
}: {
  api: GameStoreApi;
  stepIndexRef: RefObject<number>;
  setStepIndex: (updater: (index: number) => number) => void;
}) {
  const selectTile = useCallback(
    (tile: Coord) => {
      const step = currentStep(stepIndexRef);
      if (step.dismissible) return;
      const store = api.getState();
      if (!step.allowsTile(store, tile)) return;
      store.selectTile(tile);
    },
    [api, stepIndexRef],
  );

  const chooseBuild = useCallback(
    (pos: Coord, kind: UnitKind) => {
      const step = currentStep(stepIndexRef);
      if (step.dismissible) return;
      const store = api.getState();
      if (step.buildKind && kind !== step.buildKind) return;
      if (!step.allowsTile(store, pos)) return;
      store.chooseBuild(pos, kind);
    },
    [api, stepIndexRef],
  );

  const endTurn = useCallback(() => {
    const step = currentStep(stepIndexRef);
    if (step.dismissible) return;
    const store = api.getState();
    if (!step.enableEndTurn || !canEndTurnFromState(store)) return;
    submitTutorialAiTurn(store, step.aiTurnMoves);
  }, [api, stepIndexRef]);

  const dismissCoach = useCallback(() => {
    const step = currentStep(stepIndexRef);
    if (!step.dismissible) return;
    setStepIndex((i) => Math.min(i + 1, TUTORIAL_STEPS.length - 1));
  }, [stepIndexRef, setStepIndex]);

  const undoLastMove = useTutorialUndo(api, stepIndexRef);

  return { selectTile, chooseBuild, endTurn, undoLastMove, dismissCoach };
}

export function useTutorialController(): TutorialController {
  const api = useGameStoreApi();
  const { stepIndex, stepIndexRef, setStepIndex } = useTutorialStepIndex(api);
  const step = TUTORIAL_STEPS[stepIndex]!;
  const selectionMode = useGameSelector((s) => s.selection.mode);
  const canEndTurnNow = useGameSelector(canEndTurnFromState);
  const canUndoNow = useGameSelector(canUndoFromState);
  const { selectTile, chooseBuild, endTurn, undoLastMove, dismissCoach } = useTutorialActions({
    api,
    stepIndexRef,
    setStepIndex,
  });

  const prompt = useMemo(() => step.prompt(selectionMode), [step, selectionMode]);

  return {
    prompt,
    isFinal: Boolean(step.isFinal),
    blocksInteraction: Boolean(step.dismissible),
    canEndTurn: step.enableEndTurn && canEndTurnNow,
    canUndo: Boolean(step.enableUndo) && canUndoNow,
    selectTile,
    chooseBuild,
    endTurn,
    undoLastMove,
    dismissCoach,
    buildKind: step.buildKind,
  };
}
